import { FC, ReactNode } from 'react'
import { Modal } from './Modal'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { PrimaryButton, SecondaryButton } from './Buttons'

interface ModalQuestionProps {
  open: boolean
  title: string
  setAccepts: React.Dispatch<React.SetStateAction<boolean>>
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  classNames?: string
  /** Texto del botón de confirmar. Default "Aceptar". */
  acceptLabel?: string
  /** Texto del botón de cancelar. Default "Cerrar". */
  cancelLabel?: string
  /**
   * Contenido extra entre el título y los botones — por ejemplo un campo de motivo.
   * Aditivo: los llamadores que no lo pasan se ven exactamente igual que antes.
   *
   * El estado de lo que pongas aquí vive en el PADRE, no en el modal: al aceptar,
   * `setOpen(false)` corre antes que `setAccepts(true)`, así que para cuando la
   * acción se dispara este componente ya se está desmontando.
   */
  children?: ReactNode
  /**
   * Deshabilita el botón de confirmar. Para cuando el contenido de `children`
   * todavía no es válido.
   */
  acceptDisabled?: boolean
  /**
   * Reemplaza el icono por defecto. El triángulo amarillo es correcto para una
   * pregunta, pero DESIGN.md reserva la familia dorada para COD y para los estados
   * de éxito de importación masiva: en una acción destructiva estaría usando el
   * color de "salió bien" para avisar de una destrucción.
   */
  icon?: ReactNode
  /**
   * Tono del botón de confirmar. `danger` para acciones destructivas.
   *
   * No es `PrimaryButton` con una clase encima: `PrimaryButton` marca el estado
   * deshabilitado con `opacity-50`, y sobre rojo eso queda rosa lavado — se lee
   * como un estado raro, no como "no disponible". En `danger` el deshabilitado va
   * en gris.
   */
  acceptTone?: 'primary' | 'danger'
}

export const ModalQuestion: FC<ModalQuestionProps> = ({
  open,
  title,
  setOpen,
  setAccepts,
  classNames,
  acceptLabel = 'Aceptar',
  cancelLabel = 'Cerrar',
  children,
  acceptDisabled = false,
  icon,
  acceptTone = 'primary',
}) => {
  const acceptClassName =
    acceptTone === 'danger'
      ? [
          'w-full rounded-md px-6 py-2.5 text-sm font-medium text-white sm:w-auto',
          // Anillo de foco propio: al no ser `PrimaryButton` este botón no hereda
          // el suyo, y quedarse sin indicador de foco justo en la acción más
          // destructiva de la pantalla es el peor lugar para perderlo.
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2',
          acceptDisabled
            ? 'cursor-not-allowed bg-gray-300'
            : 'bg-red-600 hover:bg-red-700',
        ].join(' ')
      : 'w-full px-6 py-2.5 sm:w-auto'

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      /**
       * Antes: `maxWidth: 20rem` con `height: 15rem` FIJO. Cualquier pregunta
       * más larga que una línea se salía de la caja o quedaba apretada contra
       * los botones. Ahora el alto lo pone el contenido y el ancho es un techo,
       * no una medida.
       *
       * `w-full` es obligatorio: el panel del Modal es un flex item con
       * `width: auto`, así que sin esto se encoge al contenido y `maxWidth` no
       * hace nada.
       */
      className={`w-full max-w-[95vw] mx-2 my-1 ${classNames ?? ''}`}
      style={{ maxWidth: '30rem' }}
    >
      <div className="flex flex-col items-center py-2">
        {icon ?? (
          <ExclamationTriangleIcon className="h-14 w-14 shrink-0 text-yellow-400" />
        )}

        {/* `whitespace-pre-line` para respetar los \n de los mensajes largos, y
            `break-words` para que un número de carga sin espacios no desborde. */}
        <p className="mt-4 whitespace-pre-line break-words text-center text-base font-semibold text-gray-800">
          {title}
        </p>

        {children ? <div className="mt-4 w-full">{children}</div> : null}

        {/* `w-full` + botones a ancho completo en móvil: una etiqueta como
            "Eliminar y anular 247 guías" no cabe junto a "Cancelar" en 375px.
            `py-2.5` deja el target en 44px (WCAG 2.5.5), la misma corrección que
            ya se aplicó al sidebar. */}
        <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-center">
          {acceptTone === 'danger' ? (
            <button
              type="button"
              className={acceptClassName}
              disabled={acceptDisabled}
              onClick={() => {
                setOpen(false)
                setAccepts(true)
              }}
            >
              {acceptLabel}
            </button>
          ) : (
            <PrimaryButton
              className={acceptClassName}
              disabled={acceptDisabled}
              onClick={() => {
                setOpen(false)
                setAccepts(true)
              }}
            >
              {acceptLabel}
            </PrimaryButton>
          )}
          <SecondaryButton
            className="w-full px-6 py-2.5 sm:w-auto"
            onClick={() => {
              setOpen(false)
              setAccepts(false)
            }}
          >
            {cancelLabel}
          </SecondaryButton>
        </div>
      </div>
    </Modal>
  )
}
